import React, { useMemo, useCallback, useEffect, useRef } from "react"
import { renderToStaticMarkup } from "react-dom/server"
import Timeline, { TimelineHeaders, SidebarHeader, DateHeader } from "react-calendar-timeline"
import "react-calendar-timeline/dist/style.css"
import { Box, HStack, Heading, VStack, Skeleton } from "@chakra-ui/react"
import { AssignButton, NewButton } from "@hydrologiq/shared/content/components/Button"
import { endOfDay, startOfDay } from "date-fns"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { FaTruck, FaWrench, FaDroplet, FaBoxOpen, FaBox, FaPause } from "react-icons/fa6"
import "./TimelineView.scss"
import { format as formatDate } from "date-fns"
import ActivityCreateWorkflow from "../activities/ActivityCreateWorkflow"
import ActivityCellHover from "./ActivityCellHover"
import AIPlanCellHover from "./AIPlanCellHover"
import AssetCreatePopover from "../creation/AssetCreatePopover"
import AssetEdit from "@hydrologiq/shared/asset/AssetEdit"
import { formatActivityKind } from "@hydrologiq/shared/utils/activities/activityStatus"
import AssetLink from "@hydrologiq/shared/asset/AssetLink"

const formatDateTime = (date: number | string | null | undefined): string => {
  if (!date) return "—"
  const dateObj = new Date(date)
  if (isNaN(dateObj.getTime())) return "—"
  return formatDate(dateObj, "dd/MM/yyyy HH:mm")
}

// Activity kind color configuration
export const ACTIVITY_KIND_CONFIGS = {
  fulfilment: {
    backgroundColor: "#C6F6D5",
    textColor: "#1E6D3F",
    icon: FaBox,
  },
  filling: {
    backgroundColor: "#BEE3F8",
    textColor: "#19596D",
    icon: FaDroplet,
  },
  movement: {
    backgroundColor: "#E9D8FD",
    textColor: "#3C2A6B",
    icon: FaTruck,
  },
  inspection: {
    backgroundColor: "#FEEBC8",
    textColor: "#7A3E00",
    icon: FaBoxOpen,
  },
  maintenance: {
    backgroundColor: "#FED7D7",
    textColor: "#82001A",
    icon: FaWrench,
  },
  hold: {
    backgroundColor: "#E6E6E6",
    textColor: "#444444",
    icon: FaPause,
  },
} as const

// Type definitions
interface Location {
  id: string
  name: string
  lat: number
  long: number
}

interface Site {
  id: string
  name: string
  location: Location | null
}

interface ActivityAttributeSite {
  id: string
  name: string
}

interface ActivityAttributeBatch {
  id: string
  name: string
}

interface ActivityAttributeAsset {
  id: string
  name: string
}

export interface ActivityAttribute {
  id: string
  attribute: string
  value?: string | null
  event_kind: string
  site?: ActivityAttributeSite | null
  batch?: ActivityAttributeBatch | null
  asset?: ActivityAttributeAsset | null
}

export interface Activity {
  id: string
  kind: gqlTypes.ActivityKind
  status: gqlTypes.ActivityStatus
  start: string
  end: string
  order_id?: string | null
  has_collection?: boolean
  has_delivery?: boolean
  order?: { id: string; name: string } | null
  customers?: Array<{ id: string; name: string }>
  site?: { id: string; name: string } | null
  source_site?: { id: string; name: string } | null
  destination_site?: { id: string; name: string } | null
  quantity?: number | null
  note?: string | null
  attributes?: ActivityAttribute[] | null
}

// interface Asset {
//   id: string
//   name: string
//   category: "hydrogen"
//   class: "storage" | "power" | "monitoring" | "gas"
//   kind: string
//   status: string
//   location: Location | null
//   reserved_site?: Site | null
//   image_url?: string | null
//   brand_url?: string | null
//   volume?: number | null
//   min_pressure?: number | null
//   created_at?: string
// }

interface AssetCalendarData {
  asset: gqlTypes.Asset
  activities: Activity[]
}

interface ApiResponse {
  data: AssetCalendarData[]
  meta: {
    page: number
    per_page: number
    total_assets: number
    total_pages: number
    date_range: {
      start: string
      end: string
      days: number
    }
  }
}

interface TimelineViewProps {
  data: ApiResponse
  orderStartDate: string
  orderEndDate: string
  visibleTimeStart: number
  visibleTimeEnd: number
  filteredAssetIds: string[]
  selectedAssetIds: string[]
  activeOrderStatuses: Set<string>
  selectedAssetId?: string // For highlighting pre-selected asset
  excludedAssetIds?: string[] // Assets that are already selected (to be marked as unavailable)
  showActivities?: boolean // Toggle to show/hide activities
  showOrderActivities?: boolean // Toggle to show/hide order-related activities
  activeActivityKinds?: Record<string, boolean> // Filter which activity kinds to show
  booking?: boolean
  isRefreshing?: boolean // Whether data is being refreshed (disables scroll interactions)
  onTimeChange: (
    visibleTimeStart: number,
    visibleTimeEnd: number,
    updateScrollCanvas: (start: number, end: number) => void
  ) => void
  onAssetSelect: (asset: gqlTypes.Asset, orderStart: string, orderEnd: string) => void
  onAssetUnselect?: (assetId: string) => void
  onItemSelect: (itemId: string) => void
  onActivityCreated?: () => void // Callback to refresh data after activity creation
  onActivityEdit?: (activityId: string, assetId: string) => void // Callback to open activity edit dialog
  highlightedAssetIds?: string[] // Assets to highlight from filtered order
  highlightedOrderDates?: { start: string; end: string } // Order date range to highlight
  aiFulfilmentPlans?: gqlTypes.FulfilmentPlan[] // AI-generated fulfilment plans to display with opacity
  multipleAssets?: boolean // Whether to allow multiple assets to be selected
}

const ActivityHoverOverlay = React.memo(
  React.forwardRef((_, ref) => {
    const [state, setState] = React.useState<{
      activity: any
      left: number
      top: number
      settings: any
      visible: boolean
    }>({
      activity: null,
      left: 0,
      top: 0,
      settings: null,
      visible: false,
    })

    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    React.useImperativeHandle(ref, () => ({
      show: (activity: Activity, left: number, top: number, settings: any) => {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
          hideTimeoutRef.current = null
        }
        setState({ activity, left, top, settings, visible: true })
      },
      hide: () => {
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
        hideTimeoutRef.current = setTimeout(() => {
          setState((s) => ({ ...s, visible: false }))
        }, 50)
      },
      updatePosition: (left: number, top: number | null) => {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
          hideTimeoutRef.current = null
        }
        setState((s) => ({ ...s, left, top: top ?? s.top }))
      },
    }))

    if (!state.visible || !state.activity || !state.settings) return null

    return (
      <ActivityCellHover
        activity={state.activity}
        icon={state.settings.icon}
        left={state.left}
        top={state.top}
        color={state.settings.textColor}
        backgroundColor={state.settings.backgroundColor}
        open={true}
      >
        <Box />
      </ActivityCellHover>
    )
  })
)

const AIPlanHoverOverlay = React.memo(
  React.forwardRef((_, ref) => {
    const [state, setState] = React.useState<{
      plan: gqlTypes.FulfilmentPlan | null
      left: number
      top: number
      visible: boolean
    }>({
      plan: null,
      left: 0,
      top: 0,
      visible: false,
    })

    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    React.useImperativeHandle(ref, () => ({
      show: (plan: gqlTypes.FulfilmentPlan, left: number, top: number) => {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
          hideTimeoutRef.current = null
        }
        setState({ plan, left, top, visible: true })
      },
      hide: () => {
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
        hideTimeoutRef.current = setTimeout(() => {
          setState((s) => ({ ...s, visible: false }))
        }, 50)
      },
      updatePosition: (left: number, top: number | null) => {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
          hideTimeoutRef.current = null
        }
        setState((s) => ({ ...s, left, top: top ?? s.top }))
      },
    }))

    if (!state.visible || !state.plan) return null

    return (
      <AIPlanCellHover plan={state.plan} left={state.left} top={state.top} open={true}>
        <Box />
      </AIPlanCellHover>
    )
  })
)
const TimelineView: React.FC<TimelineViewProps> = ({
  data,
  orderStartDate,
  orderEndDate,
  visibleTimeStart,
  visibleTimeEnd,
  filteredAssetIds,
  selectedAssetIds,
  activeOrderStatuses,
  selectedAssetId,
  excludedAssetIds = [],
  showActivities = true,
  showOrderActivities = false,
  activeActivityKinds,
  booking = false,
  isRefreshing = false,
  onTimeChange,
  onAssetSelect,
  onAssetUnselect,
  onActivityCreated,
  onActivityEdit,
  highlightedAssetIds = [],
  highlightedOrderDates,
  aiFulfilmentPlans = [],
  multipleAssets = false,
}) => {
  // Ref for controlling the activity hover overlay (avoids re-rendering TimelineView)
  const hoverOverlayRef = useRef<any>(null)
  const aiPlanHoverOverlayRef = useRef<any>(null)
  // State for tracking hovered day/asset for activity creation
  const [hoveredCell, setHoveredCell] = React.useState<{
    assetId: string
    date: Date
    time: number
    left: number
    top: number
  } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // State for activity creation dialog
  const [activityDialogOpen, setActivityDialogOpen] = React.useState(false)
  const [activityDialogData, setActivityDialogData] = React.useState<{ assetId: string; date: Date } | null>(null)

  // Wrap onTimeChange to disable scrolling while refreshing
  const handleTimeChange = useCallback(
    (
      newVisibleTimeStart: number,
      newVisibleTimeEnd: number,
      updateScrollCanvas: (start: number, end: number) => void
    ) => {
      // Ignore scroll events while refreshing
      if (isRefreshing) {
        // Reset scroll canvas to current position to prevent visual jump
        updateScrollCanvas(visibleTimeStart, visibleTimeEnd)
        return
      }
      onTimeChange(newVisibleTimeStart, newVisibleTimeEnd, updateScrollCanvas)
    },
    [isRefreshing, onTimeChange, visibleTimeStart, visibleTimeEnd]
  )

  useEffect(() => {
    if (!activityDialogOpen) {
      setHoveredCell(null)
    }
  }, [activityDialogOpen])

  // Helper to check if asset has conflicts (activities) in the order date range
  const hasBookingConflict = useCallback(
    (assetData: AssetCalendarData) => {
      if (!booking) return false
      const orderStart = startOfDay(new Date(orderStartDate)).getTime()
      const orderEnd = endOfDay(new Date(orderEndDate)).getTime()

      // Check for overlapping activities
      const hasActivity = (assetData.activities || []).some((activity) => {
        const activityStart = new Date(activity.start).getTime()
        const activityEnd = new Date(activity.end).getTime()

        // Check for overlap: activity overlaps if it starts before order ends AND ends after order starts
        return activityStart <= orderEnd && activityEnd >= orderStart
      })

      return hasActivity
    },
    [orderStartDate, orderEndDate]
  )

  // Helper function to check if asset is available for the order date range
  const isAssetAvailable = useCallback((assetData: any, orderStart: string, orderEnd: string) => {
    if (!booking) return true
    const orderStartTime = startOfDay(new Date(orderStart)).getTime()
    const orderEndTime = endOfDay(new Date(orderEnd)).getTime()

    // Check for overlapping activities
    const hasActivityConflict = (assetData.activities || []).some((activity: any) => {
      const activityStart = new Date(activity.start).getTime()
      const activityEnd = new Date(activity.end).getTime()

      // Check for overlap: activity overlaps if it starts before order ends AND ends after order starts
      return activityStart <= orderEndTime && activityEnd >= orderStartTime
    })

    return !hasActivityConflict
  }, [])

  // Transform API data to timeline format - matching original styling
  const { groups, items } = useMemo(() => {
    const filteredData = data.data
      .filter((d) => filteredAssetIds.length === 0 || filteredAssetIds.includes(d.asset.id))
      .sort((a, b) => {
        // Selected asset always first
        const aIsSelected = selectedAssetId === a.asset.id
        const bIsSelected = selectedAssetId === b.asset.id

        if (aIsSelected !== bIsSelected) {
          return aIsSelected ? -1 : 1
        }

        // Highlighted assets (from order filter) next
        const aIsHighlighted = highlightedAssetIds.includes(a.asset.id)
        const bIsHighlighted = highlightedAssetIds.includes(b.asset.id)

        if (aIsHighlighted !== bIsHighlighted) {
          return aIsHighlighted ? -1 : 1
        }

        // Check availability for order date range
        const aAvailable = isAssetAvailable(a, orderStartDate, orderEndDate)
        const bAvailable = isAssetAvailable(b, orderStartDate, orderEndDate)

        // Available assets first
        if (aAvailable !== bAvailable) {
          return aAvailable ? -1 : 1
        }

        // Then sort alphabetically by name
        return a.asset.name.localeCompare(b.asset.name)
      })

    const timelineGroups = filteredData.map((assetData) => ({
      id: assetData.asset.id,
      title: assetData.asset.name,
      asset: assetData.asset,
      height: 60,
      isHighlighted: highlightedAssetIds.includes(assetData.asset.id),
    }))

    // Transform activities into timeline items
    const timelineItems = filteredData.flatMap((assetData) => {
      // Activities (only if showActivities is true)
      // All activities are now shown, including fulfilments (kind='fulfilment')
      const filteredActivities = showActivities
        ? (assetData.activities || []).filter((a) =>
            activeActivityKinds ? activeActivityKinds[a.kind] !== false : true
          )
        : []

      // Group activities by day to handle display logic
      // For multi-day activities, we need to create entries for each day they span
      const activitiesByDay = new Map<
        string,
        Array<(typeof filteredActivities)[0] & { originalActivity: (typeof filteredActivities)[0] }>
      >()

      filteredActivities.forEach((activity) => {
        const activityStart = new Date(activity.start)
        const activityEnd = new Date(activity.end)
        const startDay = startOfDay(activityStart)
        const endDay = startOfDay(activityEnd)

        // If activity spans multiple days, create entries for each day
        const currentDay = new Date(startDay)
        while (currentDay <= endDay) {
          const dayKey = currentDay.toDateString()
          if (!activitiesByDay.has(dayKey)) {
            activitiesByDay.set(dayKey, [])
          }
          // Store reference to original activity for each day it spans
          activitiesByDay.get(dayKey)!.push({
            ...activity,
            originalActivity: activity,
          })
          currentDay.setDate(currentDay.getDate() + 1)
        }
      })

      // Sort activities within each day by start time
      activitiesByDay.forEach((activities) => {
        activities.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
      })

      const activityItems = Array.from(activitiesByDay.entries()).flatMap(([dayKey, dayActivities]) => {
        const dayStart = startOfDay(new Date(dayKey)).getTime()
        const dayEnd = endOfDay(new Date(dayKey)).getTime()
        const dayDuration = dayEnd - dayStart

        if (dayActivities.length === 1) {
          // Single activity: normalize to span the full day for better visibility
          const activity = dayActivities[0]

          return {
            id: `activity_${assetData.asset.id}_${activity.id}_${dayKey}`,
            group: assetData.asset.id,
            title: activity.kind.charAt(0).toUpperCase() + activity.kind.slice(1).replace("_", " "),
            start_time: dayStart,
            end_time: dayEnd,
            itemProps: {
              "data-activity-id": activity.id,
              "data-activity-kind": activity.kind,
              "data-activity-status": activity.status,
              "data-type": "activity",
            } as any,
            activity: activity,
            asset: assetData.asset,
            itemType: "activity" as const,
          }
        } else {
          // Multiple activities: split the day evenly between them, normalized to their slots
          const slotDuration = dayDuration / dayActivities.length
          return dayActivities.map((activity, index) => {
            // Calculate the normalized slot for this activity
            const slotStart = dayStart + index * slotDuration
            const slotEnd = dayStart + (index + 1) * slotDuration

            return {
              id: `activity_${assetData.asset.id}_${activity.id}_${dayKey}_${index}`,
              group: assetData.asset.id,
              start_time: slotStart,
              end_time: slotEnd,
              itemProps: {
                "data-activity-id": activity.id,
                "data-activity-kind": activity.kind,
                "data-activity-status": activity.status,
                "data-type": "activity",
              } as any,
              activity: activity,
              asset: assetData.asset,
              itemType: "activity" as const,
            }
          })
        }
      })

      // Create placeholder items for empty days (to enable hover/plus icon)
      // Calculate all days in the visible range
      const startDay = startOfDay(new Date(visibleTimeStart))
      const endDay = endOfDay(new Date(visibleTimeEnd))
      const placeholderItems: any[] = []

      const currentDay = new Date(startDay)
      while (currentDay <= endDay) {
        const dayStart = startOfDay(currentDay).getTime()
        const dayEnd = endOfDay(currentDay).getTime()

        // Check if this day already has items for this asset
        const hasItemsForDay = activityItems.some((item) => {
          const itemStart = item.start_time
          const itemEnd = item.end_time
          // Check if there's any overlap
          return itemStart <= dayEnd && itemEnd >= dayStart
        })

        // If no items, create a placeholder
        if (!hasItemsForDay) {
          placeholderItems.push({
            id: `placeholder_${assetData.asset.id}_${currentDay.toISOString()}`,
            group: assetData.asset.id,
            start_time: dayStart,
            end_time: dayEnd,
            itemProps: {
              "data-type": "placeholder",
            } as any,
            itemType: "placeholder" as const,
          })
        }

        currentDay.setDate(currentDay.getDate() + 1)
      }

      return [...activityItems, ...placeholderItems]
    })

    // Add AI fulfilment plans as semi-transparent items
    const aiPlanItems = aiFulfilmentPlans.map((plan) => ({
      id: `ai-${plan.id}`,
      group: plan.asset.id,
      title: `[AI] ${plan.order.name}: ${plan.quantity}kg`,
      start_time: new Date(plan.start).getTime(),
      end_time: new Date(plan.end).getTime(),
      itemType: "activity" as const,
      itemProps: {
        "data-activity-id": `ai-plan-${plan.id}`,
        "data-activity-kind": "fulfilment",
        "data-is-ai-plan": true,
        style: {
          opacity: 0.5,
          border: "2px dashed #8B5CF6",
        },
      },
      aiPlan: plan,
    }))

    const allItems = [...timelineItems, ...aiPlanItems]

    return { groups: timelineGroups, items: allItems }
  }, [
    data,
    filteredAssetIds,
    activeOrderStatuses,
    isAssetAvailable,
    orderStartDate,
    orderEndDate,
    selectedAssetId,
    selectedAssetIds,
    excludedAssetIds,
    showActivities,
    showOrderActivities,
    activeActivityKinds,
    visibleTimeStart,
    visibleTimeEnd,
    highlightedAssetIds,
    aiFulfilmentPlans,
  ])

  // Custom group renderer - with conflict detection and highlighting
  const groupRenderer = useCallback(
    ({ group }: { group: any }) => {
      const asset = group.asset as gqlTypes.Asset
      const assetData = data.data.find((d) => d.asset.id === asset.id)
      const hasConflict = assetData ? hasBookingConflict(assetData) : false
      const isSelected = selectedAssetId === asset.id
      const isExcluded = excludedAssetIds.includes(asset.id)
      const canUnselect = selectedAssetIds.includes(asset.id)
      const isHighlighted = group.isHighlighted || false

      return (
        <Box
          style={{
            fontSize: "14px",
            padding: "8px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            cursor: booking ? undefined : isExcluded || hasConflict ? "not-allowed" : "pointer",
            borderRight: "1px solid #e2e8f0",
            backgroundColor: isExcluded
              ? "#FFE5E5" // Light pink/rose background for excluded (already assigned)
              : isSelected
                ? "#EBF8FF" // Light blue background for selected
                : isHighlighted
                  ? "#FFF4E6" // Light orange background for highlighted from order filter
                  : undefined,
            borderLeft: canUnselect
              ? "3px solid #8f2eff"
              : isHighlighted
                ? "3px solid #FF6B35" // Orange border for highlighted
                : undefined,
            opacity: booking && (canUnselect || hasConflict) ? 0.7 : 1,
          }}
        >
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <HStack width="100%">
              {booking && (
                <AssignButton
                  onClick={() => {
                    if (!hasConflict && !canUnselect) {
                      onAssetSelect(asset, orderStartDate, orderEndDate)
                    } else if (canUnselect) {
                      onAssetUnselect?.(asset.id)
                    }
                  }}
                  disabled={
                    (hasConflict && !canUnselect) || (!multipleAssets && selectedAssetIds.length >= 1 && !canUnselect)
                  }
                  context={
                    canUnselect
                      ? {
                        label: "Unassign",
                        description: "Unassign this asset from the fulfilment",
                        variant: "secondary",
                      }
                      : undefined
                  }
                  title={
                    isExcluded
                      ? "Asset is already added to this fulfilment"
                      : hasConflict
                        ? "Asset is already booked during this time period"
                        : "Assign this asset to the fulfilment"
                  }
                />
              )}

              <HStack justifyContent="space-between" width="100%">
                <AssetLink asset={asset} openExternal={true} />
                <HStack>
                  <AssetEdit asset={asset} onUpdate={() => { }} />
                  <NewButton
                    context={{ label: "New", description: "Create an activity or fulfilment" }}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setActivityDialogData({
                        assetId: asset.id,
                        date: new Date(),
                      })
                      setActivityDialogOpen(true)
                    }}
                  />
                </HStack>
              </HStack>
            </HStack>
          </Box>
        </Box>
      )
    },
    [
      onAssetSelect,
      onAssetUnselect,
      orderStartDate,
      orderEndDate,
      data.data,
      hasBookingConflict,
      selectedAssetId,
      selectedAssetIds,
      excludedAssetIds,
      booking,
      highlightedAssetIds,
    ]
  )

  // Custom item renderer - using status icons and status-specific colors
  const itemRenderer = useCallback(
    ({ item, itemContext, getItemProps }: any) => {
      const { style, ...otherProps } = getItemProps(item.itemProps)

      // Parse the width from the style object
      const originalWidth = typeof style.width === "string" ? parseFloat(style.width) : style.width
      const adjustedWidth = originalWidth
      const originalLeft = typeof style.left === "string" ? parseFloat(style.left) : style.left
      const adjustedLeft = originalLeft

      // Determine if this is a fulfilment, order, or activity
      const itemType = item.itemType || "fulfilment"

      let settings: { backgroundColor: string; textColor: string; icon: any }
      let clickableContent: React.ReactNode

      if (itemType === "placeholder") {
        // Placeholder item - render as a subtle background to indicate available space
        clickableContent = (
          <Box
            {...otherProps}
            style={{
              ...style,
              width: adjustedWidth,
              left: adjustedLeft,
              cursor: "default",
              border: "none",
              backgroundColor: "rgba(229, 231, 235, 0.3)", // Very light gray, semi-transparent
              marginTop: "-6.5px",
              boxSizing: "border-box",
              zIndex: 1, // Lowest z-index so everything appears above
            }}
          />
        )
      } else if (itemType === "activity") {
        // Activity item - use activity kind colors
        const activityKind = item.itemProps["data-activity-kind"] as keyof typeof ACTIVITY_KIND_CONFIGS
        const isAIPlan = item.itemProps["data-is-ai-plan"] === true
        settings = ACTIVITY_KIND_CONFIGS[activityKind] || ACTIVITY_KIND_CONFIGS.maintenance
        if (!settings)
          settings = {
            backgroundColor: "#E9D8FD",
            textColor: "#3C2A6B",
            icon: FaBox,
          }

        // For fulfilment activities, check collection/delivery status
        const isfulfilment = activityKind === "fulfilment"
        const hasCollection = item.activity?.has_collection || false
        const hasDelivery = item.activity?.has_delivery || false
        const missingCollection = isfulfilment && !hasCollection && !isAIPlan
        const missingDelivery = isfulfilment && !hasDelivery && !isAIPlan
        // Find all activities with the same ID, sorted by start time, and determine if this is the first or last
        const sameActivityItems = items
          .filter((i) => i.itemProps["data-activity-id"] === item.itemProps["data-activity-id"])
          .sort((a, b) => a.start_time - b.start_time)
        const currentActivityIndex = sameActivityItems.findIndex((i) => i.id === item.id)
        const isFirstActivity = currentActivityIndex === 0
        const isLastActivity = currentActivityIndex === sameActivityItems.length - 1

        // AI plans have distinct styling with 50% opacity
        const aiPlanStyles = isAIPlan
          ? {
            opacity: 0.66,
          }
          : {}

        const borderRadius = "6px"
        // Activities are clickable to open edit dialog (only if not planned or AI)
        clickableContent = (
          <Box
            {...otherProps}
            aria-label={isAIPlan ? undefined : `${item.asset?.name} ${formatActivityKind(item.activity?.kind)} (${formatDateTime(
              item.activity?.start
            )} - ${formatDateTime(item.activity?.end)}) Day ${currentActivityIndex + 1}`}
            role={"tooltip"}
            style={{
              ...style,
              width: adjustedWidth,
              left: adjustedLeft,
              cursor: isAIPlan ? "default" : "pointer",
              borderRadius: isAIPlan ? borderRadius : "0px !important",
              border: isAIPlan ? "2px dashed #8B5CF6" : "none",
              backgroundColor: settings.backgroundColor,
              color: settings.textColor,
              marginTop: "-6.5px",
              boxSizing: "border-box",
              fontSize: "14px",
              fontWeight: 500,
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              zIndex: isfulfilment ? 5 : 10,
              // Add accent left border (red if missing delivery, otherwise activity color)
              borderLeft: isFirstActivity
                ? missingDelivery
                  ? "4px solid #EF4444"
                  : `4px solid ${settings.textColor}`
                : undefined,
              // Add red right border if missing delivery
              borderRight: isLastActivity && missingCollection ? "4px solid #EF4444" : undefined,
              borderTopRightRadius: isAIPlan ? undefined : (isLastActivity ? borderRadius : undefined),
              borderBottomRightRadius: isAIPlan ? undefined : (isLastActivity ? borderRadius : undefined),
              borderTopLeftRadius: isAIPlan ? undefined : (isFirstActivity ? borderRadius : undefined),
              borderBottomLeftRadius: isAIPlan ? undefined : (isFirstActivity ? borderRadius : undefined),
              ...aiPlanStyles,
            }}
            onMouseEnter={(e) => {
              if (isAIPlan) {
                aiPlanHoverOverlayRef.current?.show(item.aiPlan, e.clientX, e.clientY)
              } else {
                hoverOverlayRef.current?.show(item.activity, e.clientX, e.clientY, settings)
              }
            }}
            onMouseMove={(e) => {
              if (isAIPlan) {
                aiPlanHoverOverlayRef.current?.updatePosition(e.clientX, null)
              } else {
                hoverOverlayRef.current?.updatePosition(e.clientX, null)
              }
            }}
            onMouseLeave={() => {
              if (isAIPlan) {
                aiPlanHoverOverlayRef.current?.hide()
              } else {
                hoverOverlayRef.current?.hide()
              }
            }}
            onClick={(e) => {
              hoverOverlayRef.current?.hide()
              if (isAIPlan) return // Don't allow editing AI plans
              e.stopPropagation()
              const activityId = item.itemProps["data-activity-id"]
              const assetId = item.asset?.id
              if (activityId && assetId) {
                onActivityEdit?.(activityId, assetId)
              }
            }}
            _hover={{
              opacity: isAIPlan ? (isAIPlan ? 0.5 : 0.7) : 0.9,
              boxShadow: isAIPlan ? "0 1px 3px 0 rgba(0, 0, 0, 0.1)" : "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          />
        )
      }

      return clickableContent
    },
    [groups, items]
  )

  // Render activity text labels as DOM overlays
  useEffect(() => {
    if (!containerRef.current) return

    const scrollContainer = containerRef.current.querySelector(".rct-scroll")
    if (!scrollContainer) return

    // Function to render overlays
    const renderOverlays = () => {
      // Remove existing text overlays
      scrollContainer.querySelectorAll(".activity-text-overlay").forEach((el) => el.remove())

      // Group items by activity ID to find bounds
      const activityBounds = new Map<
        string,
        {
          minLeft: number
          maxRight: number
          top: number
          height: number
          displayText: string
          settings: { backgroundColor: string; textColor: string; icon: any }
          isAIPlan: boolean
          aiPlanTitle?: string
          cellCount: number
        }
      >()

      // Get timeline dimensions for calculations
      const containerWidth = scrollContainer.clientWidth
      const totalRange = visibleTimeEnd - visibleTimeStart
      const rowHeight = 60

      // Calculate bounds for each unique activity
      items.forEach((item) => {
        if (item.itemType !== "activity") return

        const activityId = item.itemProps["data-activity-id"]
        const activityKind = item.itemProps["data-activity-kind"] as keyof typeof ACTIVITY_KIND_CONFIGS
        const isAIPlan = item.itemProps["data-is-ai-plan"] === true

        // Determine display text - always show something for all activity kinds
        let displayText = ""
        displayText = item.asset?.name ? (item.asset?.name.length > 10 ? item.asset?.name.slice(0, 10) + "..." : item.asset?.name) + " - " : ""

        if (activityKind === "fulfilment") {
          displayText += item.activity?.customers?.[0]?.name || ""
        } else if (activityKind === "filling") {
          displayText += item.activity?.site?.name || ""
        }

        // Add quantity - always show it, even if 0
        const quantity = item.activity?.quantity || 0
        displayText += quantity ? ` (${quantity} kg)` : ""

        // Calculate pixel position
        const leftPx = ((item.start_time - visibleTimeStart) / totalRange) * containerWidth
        const rightPx = ((item.end_time - visibleTimeStart) / totalRange) * containerWidth

        // Find row index
        const rowIndex = groups.findIndex((g) => g.id === item.group)
        if (rowIndex === -1) return

        const topPx = rowIndex * rowHeight

        // Get settings for this activity kind
        const settings = ACTIVITY_KIND_CONFIGS[activityKind] || ACTIVITY_KIND_CONFIGS.maintenance

        if (!activityBounds.has(activityId)) {
          activityBounds.set(activityId, {
            minLeft: leftPx,
            maxRight: rightPx,
            top: topPx,
            height: rowHeight,
            displayText,
            settings,
            isAIPlan,
            aiPlanTitle: isAIPlan ? item.title : undefined,
            cellCount: 1,
          })
        } else {
          const bounds = activityBounds.get(activityId)!
          bounds.minLeft = Math.min(bounds.minLeft, leftPx)
          bounds.maxRight = Math.max(bounds.maxRight, rightPx)
          bounds.cellCount += 1
        }
      })

      // Create overlay elements for each activity
      activityBounds.forEach((bounds, activityId) => {
        // Skip if less than 1 cell
        if (bounds.cellCount < 1) return

        const overlay = document.createElement("div")
        overlay.className = "activity-text-overlay"

        // Render the entire overlay content using React
        const IconComponent = bounds.settings.icon

        // Determine what to show based on cell count
        // <= 2 cells: show text if available, otherwise show icon
        // > 2 cells: show both icon and text
        const showIcon = bounds.cellCount > 2
        const showText = bounds.displayText || bounds.aiPlanTitle

        const overlayContent = renderToStaticMarkup(
          <div
            style={{
              position: "absolute",
              left: `${bounds.minLeft}px`,
              top: `${bounds.top}px`,
              width: `${bounds.maxRight - bounds.minLeft}px`,
              height: `${bounds.height}px`,
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              paddingLeft: "12px",
              paddingRight: "12px",
              color: bounds.settings.textColor,
              fontSize: "14px",
              fontWeight: "500",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              zIndex: 10,
              opacity: bounds.isAIPlan ? 0.66 : 1,
            }}
          >
            {showIcon && IconComponent && <IconComponent style={{ flexShrink: 0, fontSize: "12px" }} />}
            {showText && bounds.displayText && (
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{bounds.displayText}</span>
            )}
            {showText && bounds.isAIPlan && bounds.aiPlanTitle && (
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{bounds.aiPlanTitle}</span>
            )}
            {/* If <= 2 cells and no text, show icon as fallback */}
            {!showText && !showIcon && IconComponent && <IconComponent style={{ flexShrink: 0, fontSize: "12px" }} />}
          </div>
        )

        overlay.innerHTML = overlayContent
        scrollContainer.appendChild(overlay)
      })
    }

    // Initial render
    renderOverlays()

    // Set up ResizeObserver to recalculate on container size changes
    const resizeObserver = new ResizeObserver(() => {
      renderOverlays()
    })
    resizeObserver.observe(scrollContainer)

    // Cleanup function
    return () => {
      resizeObserver.disconnect()
      scrollContainer.querySelectorAll(".activity-text-overlay").forEach((el) => el.remove())
    }
  }, [items, groups, visibleTimeStart, visibleTimeEnd])

  // Add hover detection for day cells (only when booking is disabled)
  useEffect(() => {
    if (!containerRef.current || booking) return

    const scrollContainer = containerRef.current.querySelector(".rct-scroll")
    if (!scrollContainer) return

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as unknown as MouseEvent
      const rect = scrollContainer.getBoundingClientRect()
      const x = mouseEvent.clientX - rect.left
      const y = mouseEvent.clientY - rect.top

      // Calculate which day is being hovered based on x position
      const containerWidth = scrollContainer.clientWidth
      const totalRange = visibleTimeEnd - visibleTimeStart
      const hoveredTime = visibleTimeStart + (x / containerWidth) * totalRange
      const hoveredDate = new Date(hoveredTime)

      // Calculate which activity row is being hovered based on y position
      // Each row is 70px tall (lineHeight prop)
      const rowHeight = 60
      const rowIndex = Math.floor(y / rowHeight)

      // Get the asset at this row index
      if (rowIndex >= 0 && rowIndex < groups.length) {
        const group = groups[rowIndex]
        setHoveredCell({
          assetId: group.id as string,
          date: startOfDay(hoveredDate),
          time: hoveredTime,
          left: mouseEvent.clientX,
          top: mouseEvent.clientY,
        })
      } else {
        setHoveredCell(null)
      }
    }

    const handleMouseLeave = () => {
      setHoveredCell(null)
    }

    scrollContainer.addEventListener("mousemove", handleMouseMove)
    scrollContainer.addEventListener("mouseleave", handleMouseLeave)

    return () => {
      scrollContainer.removeEventListener("mousemove", handleMouseMove)
      scrollContainer.removeEventListener("mouseleave", handleMouseLeave)
    }
  }, [booking, visibleTimeStart, visibleTimeEnd, groups])

  // Render "+" icon overlay when hovering over a day cell
  useEffect(() => {
    if (!containerRef.current || booking || !hoveredCell) return

    const scrollContainer = containerRef.current.querySelector(".rct-scroll")
    if (!scrollContainer) return

    // Calculate position for the "+" icon
    const containerWidth = scrollContainer.clientWidth
    const totalRange = visibleTimeEnd - visibleTimeStart

    // Get the day boundaries
    const dayStart = startOfDay(hoveredCell.date).getTime()
    const dayEnd = endOfDay(hoveredCell.date).getTime()

    // Calculate pixel positions
    const leftPx = ((dayStart - visibleTimeStart) / totalRange) * containerWidth
    const rightPx = ((dayEnd - visibleTimeStart) / totalRange) * containerWidth
    const widthPx = rightPx - leftPx

    // Get row index for this asset
    const rowIndex = groups.findIndex((g) => g.id === hoveredCell.assetId)
    if (rowIndex === -1) return

    const rowHeight = 60
    const topPx = rowIndex * rowHeight

    // Note: We always show the + icon at the top now, regardless of whether there are items

    // Create or update overlay
    let overlay = scrollContainer.querySelector(".add-activity-overlay") as HTMLElement
    if (!overlay) {
      overlay = document.createElement("div")
      overlay.className = "add-activity-overlay"
      scrollContainer.appendChild(overlay)
    }

    // Position the overlay - only set dynamic positioning (CSS handles the rest)
    overlay.style.left = `${Math.max(0, leftPx)}px`
    overlay.style.top = `${topPx}px`
    overlay.style.width = `${Math.max(0, widthPx)}px`
    overlay.style.height = `${rowHeight}px`

    // Create or update the button element (CSS handles all styling)
    let button = overlay.querySelector(".add-activity-button") as HTMLElement
    if (!button) {
      button = document.createElement("div")
      button.className = "add-activity-button"
      overlay.appendChild(button)
    }

    // Update button content if needed
    button.innerHTML = `
      <svg style="width: 18px; height: 18px; color: white;" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd"/>
      </svg>
    `

    // Remove any existing click handler to prevent duplicates
    const newButton = button
    newButton.onclick = null

    // Add click handler
    newButton.onclick = (e) => {
      e.stopPropagation() // Prevent event bubbling to timeline
      setActivityDialogData({
        assetId: hoveredCell.assetId,
        date: hoveredCell.date,
      })
      setActivityDialogOpen(true)
    }

    return () => {
      const existingOverlay = scrollContainer.querySelector(".add-activity-overlay")
      if (existingOverlay) {
        existingOverlay.remove()
      }
    }
  }, [hoveredCell, booking, visibleTimeStart, visibleTimeEnd, groups, items])

  // Add order date highlight overlay to the timeline canvas
  useEffect(() => {
    if (!containerRef.current || !booking) return

    const orderStart = startOfDay(new Date(orderStartDate)).getTime()
    const orderEnd = endOfDay(new Date(orderEndDate)).getTime()

    // Only show if order dates are within visible range
    if (orderEnd < visibleTimeStart || orderStart > visibleTimeEnd) {
      // Remove existing overlay if dates are out of range
      const existingOverlay = containerRef.current.querySelector(".order-date-highlight")
      if (existingOverlay) {
        existingOverlay.remove()
      }
      return
    }

    // Defer overlay positioning to next tick to let timeline finish rendering
    const timeoutId = setTimeout(() => {
      // Find the timeline scroll/canvas container
      const scrollContainer = containerRef.current?.querySelector(".rct-scroll")
      if (!scrollContainer) return

      // // Calculate position in pixels based on the actual scroll container width
      // const containerWidth = scrollContainer.clientWidth
      // const totalRange = visibleTimeEnd - visibleTimeStart

      // // Calculate pixel positions
      // const leftPx = ((orderStart - visibleTimeStart) / totalRange) * containerWidth
      // const rightPx = ((orderEnd - visibleTimeStart) / totalRange) * containerWidth
      // const widthPx = rightPx - leftPx

      // // Create or update overlay
      // let overlay = scrollContainer.querySelector(".order-date-highlight") as HTMLElement
      // if (!overlay) {
      //   overlay = document.createElement("div")
      //   overlay.className = "order-date-highlight"
      //   scrollContainer.appendChild(overlay)
      // }

      // Calculate position in pixels based on the actual scroll container width
      const containerWidth = scrollContainer.clientWidth
      const totalRange = visibleTimeEnd - visibleTimeStart

      // Calculate pixel positions, clamped to container bounds
      const rawLeftPx = ((orderStart - visibleTimeStart) / totalRange) * containerWidth
      const rawRightPx = ((orderEnd - visibleTimeStart) / totalRange) * containerWidth

      // Clamp to visible area
      const leftPx = Math.max(0, rawLeftPx)
      const rightPx = Math.min(containerWidth, rawRightPx)
      const widthPx = Math.max(0, rightPx - leftPx)

      // Create or update overlay
      let overlay = scrollContainer.querySelector(".order-date-highlight") as HTMLElement
      if (!overlay) {
        overlay = document.createElement("div")
        overlay.className = "order-date-highlight"
        scrollContainer.appendChild(overlay)
      }

      // Set position using pixels for precise alignment
      overlay.style.left = `${Math.max(0, leftPx)}px`
      overlay.style.width = `${Math.max(0, widthPx)}px`
    }, 0)

    // Cleanup
    return () => {
      clearTimeout(timeoutId)
      const existingOverlay = containerRef.current?.querySelector(".order-date-highlight")
      if (existingOverlay) {
        existingOverlay.remove()
      }
    }
  }, [orderStartDate, orderEndDate, visibleTimeStart, visibleTimeEnd])

  // Add highlighted order date range overlay to the timeline canvas (for filtered orders)
  useEffect(() => {
    if (!containerRef.current || !highlightedOrderDates) return

    const orderStart = startOfDay(new Date(highlightedOrderDates.start)).getTime()
    const orderEnd = endOfDay(new Date(highlightedOrderDates.end)).getTime()

    // Only show if order dates are within visible range
    if (orderEnd < visibleTimeStart || orderStart > visibleTimeEnd) {
      // Remove existing overlay if dates are out of range
      const existingOverlay = containerRef.current.querySelector(".highlighted-order-date-overlay")
      if (existingOverlay) {
        existingOverlay.remove()
      }
      return
    }

    // Defer overlay positioning to next tick to let timeline finish rendering
    const timeoutId = setTimeout(() => {
      // Find the timeline scroll/canvas container
      const scrollContainer = containerRef.current?.querySelector(".rct-scroll")
      if (!scrollContainer) return

      // Remove any existing overlay
      const existingOverlay = scrollContainer.querySelector(".highlighted-order-date-overlay")
      if (existingOverlay) {
        existingOverlay.remove()
      }

      // Create new overlay element
      const overlay = document.createElement("div")
      overlay.className = "highlighted-order-date-overlay"
      overlay.style.position = "absolute"
      overlay.style.top = "0"
      overlay.style.bottom = "0"
      overlay.style.backgroundColor = "rgba(255, 107, 53, 0.15)" // Orange with transparency
      overlay.style.border = "2px solid #FF6B35"
      overlay.style.borderTop = "none"
      overlay.style.borderBottom = "none"
      overlay.style.pointerEvents = "none"
      overlay.style.zIndex = "1"

      // Calculate position using the same logic as react-calendar-timeline
      // The timeline uses pixel positions, so we calculate based on visible time range
      const timelineWidth = scrollContainer.scrollWidth
      const visibleRange = visibleTimeEnd - visibleTimeStart

      // Calculate pixel positions
      const startOffset = ((orderStart - visibleTimeStart) / visibleRange) * timelineWidth
      const endOffset = ((orderEnd - visibleTimeStart) / visibleRange) * timelineWidth
      const width = endOffset - startOffset

      overlay.style.left = `${Math.max(0, startOffset)}px`
      overlay.style.width = `${Math.max(0, width)}px`

      // Append to scroll container
      scrollContainer.appendChild(overlay)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      const existingOverlay = containerRef.current?.querySelector(".highlighted-order-date-overlay")
      if (existingOverlay) {
        existingOverlay.remove()
      }
    }
  }, [highlightedOrderDates, visibleTimeStart, visibleTimeEnd])

  // Custom keyboard navigation that accounts for zoom level
  useEffect(() => {
    if (!containerRef.current) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle arrow keys and page navigation keys
      const isArrowKey = e.key === "ArrowLeft" || e.key === "ArrowRight"
      const isPageKey = e.key === "PageUp" || e.key === "PageDown"

      if (!isArrowKey && !isPageKey) return

      // Check if we're focused on the timeline container or its children
      const timelineElement = containerRef.current?.querySelector(".react-calendar-timeline")
      if (!timelineElement?.contains(document.activeElement) && document.activeElement !== containerRef.current) return

      e.preventDefault() // Prevent default timeline navigation

      const currentRange = visibleTimeEnd - visibleTimeStart
      let moveAmount = 0

      if (isArrowKey) {
        // Arrow keys: move by 10% of current visible range
        moveAmount = currentRange * 0.1
      } else if (isPageKey) {
        // Page keys: move by 80% of current visible range (almost a full page)
        moveAmount = currentRange * 0.8
      }

      let newStart = visibleTimeStart
      let newEnd = visibleTimeEnd

      if (e.key === "ArrowLeft" || e.key === "PageUp") {
        newStart = visibleTimeStart - moveAmount
        newEnd = visibleTimeEnd - moveAmount
      } else if (e.key === "ArrowRight" || e.key === "PageDown") {
        newStart = visibleTimeStart + moveAmount
        newEnd = visibleTimeEnd + moveAmount
      }

      // Call onTimeChange with the new time range
      onTimeChange(newStart, newEnd, (start, end) => {
        // This callback would be used by the timeline library for smooth scrolling
        // but we can leave it empty since we're handling the navigation directly
      })
    }

    const timelineContainer = containerRef.current
    timelineContainer.addEventListener("keydown", handleKeyDown)

    return () => {
      timelineContainer.removeEventListener("keydown", handleKeyDown)
    }
  }, [visibleTimeStart, visibleTimeEnd, onTimeChange])

  // Validate that we have valid time values before rendering
  const hasValidTimeRange =
    !isNaN(visibleTimeStart) &&
    !isNaN(visibleTimeEnd) &&
    visibleTimeStart > 0 &&
    visibleTimeEnd > 0 &&
    visibleTimeEnd > visibleTimeStart

  if (!hasValidTimeRange) {
    return (
      <Box p={4}>
        <VStack gap={3} align="stretch">
          <Skeleton height="40px" width="100%" />
          <Skeleton height="60px" width="100%" />
          <Skeleton height="60px" width="100%" />
          <Skeleton height="60px" width="100%" />
          <Skeleton height="60px" width="100%" />
        </VStack>
      </Box>
    )
  }

  // Get the asset for the activity dialog
  const dialogAsset = React.useMemo(() => {
    if (!activityDialogData) return undefined
    const assetData = data.data.find((d) => d.asset.id === activityDialogData.assetId)
    return assetData?.asset
  }, [activityDialogData, data])

  return (
    <>
      <Box ref={containerRef} className="timeline-container" position="relative" tabIndex={0}>
        <Timeline
          groups={groups}
          items={items}
          visibleTimeStart={visibleTimeStart}
          visibleTimeEnd={visibleTimeEnd}
          minZoom={24 * 60 * 60 * 1000} // 1 day minimum zoom
          maxZoom={31 * 24 * 60 * 60 * 1000} // 31 days (1 month) maximum zoom out
          onTimeChange={handleTimeChange}
          itemRenderer={itemRenderer}
          groupRenderer={groupRenderer}
          sidebarWidth={350}
          lineHeight={70}
          itemHeightRatio={0.75}
          stackItems={false}
          canMove={false}
          canResize={false}
          canSelect={false}
          selected={[]}
          buffer={1}
          dragSnap={0}
          traditionalZoom={true}
          onCanvasClick={() => { }}
          onCanvasDoubleClick={() => { }}
          onCanvasContextMenu={() => { }}
        >
          <TimelineHeaders>
            <SidebarHeader>
              {({ getRootProps }: any) => (
                <HStack {...getRootProps()} p={3} justifyContent="space-between" width="100%">
                  <Heading color="primary">Asset</Heading>
                  <AssetCreatePopover onSuccess={() => { }} />
                </HStack>
              )}
            </SidebarHeader>
            <DateHeader unit="primaryHeader" style={{ color: "#2B2B2B !important" }} />
            <DateHeader unit="day" />
          </TimelineHeaders>
        </Timeline>
      </Box>

      {/* Activity creation dialog - key ensures new instance on each open */}
      {activityDialogData && dialogAsset && activityDialogOpen && (
        <ActivityCreateWorkflow
          key={`${activityDialogData.assetId}-${activityDialogData.date.getTime()}`}
          asset={dialogAsset as any}
          openOverride={activityDialogOpen}
          onOpenChange={setActivityDialogOpen}
          onActivityCreated={() => {
            setActivityDialogOpen(false)
            setActivityDialogData(null)
            onActivityCreated?.()
          }}
          defaultStartDate={formatDate(activityDialogData.date, "yyyy-MM-dd")}
          allowAnyAsset={false}
        />
      )}

      <ActivityHoverOverlay ref={hoverOverlayRef} />
      <AIPlanHoverOverlay ref={aiPlanHoverOverlayRef} />
    </>
  )
}

// Memoize the component to prevent re-renders when parent updates
export default React.memo(TimelineView, (prevProps, nextProps) => {
  // Deep comparison for arrays and Sets
  const filteredAssetIdsEqual =
    prevProps.filteredAssetIds.length === nextProps.filteredAssetIds.length &&
    prevProps.filteredAssetIds.every((id, index) => id === nextProps.filteredAssetIds[index])

  const activeOrderStatusesEqual =
    prevProps.activeOrderStatuses.size === nextProps.activeOrderStatuses.size &&
    Array.from(prevProps.activeOrderStatuses).every((status) => nextProps.activeOrderStatuses.has(status))

  const selectedAssetIdsEqual =
    prevProps.selectedAssetIds.length === nextProps.selectedAssetIds.length &&
    prevProps.selectedAssetIds.every((id) => nextProps.selectedAssetIds.includes(id))

  const excludedAssetIdsEqual =
    (prevProps.excludedAssetIds?.length || 0) === (nextProps.excludedAssetIds?.length || 0) &&
    (prevProps.excludedAssetIds || []).every((id) => (nextProps.excludedAssetIds || []).includes(id))

  // Return TRUE to SKIP re-render, FALSE to re-render
  const shouldSkipRender =
    prevProps.visibleTimeStart === nextProps.visibleTimeStart &&
    prevProps.visibleTimeEnd === nextProps.visibleTimeEnd &&
    filteredAssetIdsEqual &&
    activeOrderStatusesEqual &&
    selectedAssetIdsEqual &&
    excludedAssetIdsEqual &&
    prevProps.data === nextProps.data &&
    prevProps.orderStartDate === nextProps.orderStartDate &&
    prevProps.orderEndDate === nextProps.orderEndDate &&
    prevProps.selectedAssetId === nextProps.selectedAssetId &&
    prevProps.showActivities === nextProps.showActivities &&
    prevProps.showOrderActivities === nextProps.showOrderActivities &&
    prevProps.booking === nextProps.booking &&
    prevProps.onTimeChange === nextProps.onTimeChange &&
    prevProps.onAssetSelect === nextProps.onAssetSelect &&
    prevProps.onAssetUnselect === nextProps.onAssetUnselect &&
    prevProps.onActivityCreated === nextProps.onActivityCreated &&
    prevProps.onActivityEdit === nextProps.onActivityEdit

  return shouldSkipRender
})
